/**
 * Review & confirm — the step between filling Manual Deal Entry and POST /loans.
 *
 * Not a route. Edit returns to the form with values kept. Confirm & Submit
 * is the only write.
 */
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react'

import type { PossibleDuplicateDeal } from '@/services/loans'
import {
  reviewTagLabel,
  type ReviewModel,
  type ReviewTag,
} from './manualEntryReviewModel'

function tagClass(tag: ReviewTag): string {
  switch (tag) {
    case 'entered':
      return 'bg-emerald-400/15 text-emerald-200'
    case 'defaulted':
      return 'bg-amber-400/15 text-amber-200'
    case 'calculated':
      return 'bg-sky-400/15 text-sky-200'
    default: {
      const _exhaustive: never = tag
      return _exhaustive
    }
  }
}

export default function ManualEntryReview({
  dealName,
  model,
  openingNote,
  duplicates,
  saving,
  apiError,
  onEdit,
  onEditSection,
  onConfirm,
  onCreateAnyway,
}: {
  dealName: string
  model: ReviewModel
  openingNote?: string
  duplicates: PossibleDuplicateDeal[] | null
  saving: boolean
  apiError: string | null
  onEdit: () => void
  onEditSection: (sectionLabel: string) => void
  onConfirm: () => void
  onCreateAnyway: () => void
}) {
  return (
    <div className="panel" data-testid="manual-entry-review">
      <div className="panel-header flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[12px] font-medium text-stone-200">Review before you submit</p>
          <p className="text-[10px] text-stone-500">
            Confirm the system read these correctly{dealName ? ` for ${dealName}` : ''}.
          </p>
        </div>
      </div>
      <div className="panel-body space-y-3">
        {duplicates && duplicates.length > 0 ? (
          <div
            role="alert"
            className="space-y-1 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3"
            data-testid="manual-entry-duplicates"
          >
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-200">
              <AlertTriangle size={12} />
              This deal may already be on the pipeline
            </div>
            {duplicates.map((d) => (
              <p key={d.loanId} className="text-[10px] text-stone-400">
                {d.property || d.loanId}
                <span className="font-mono"> · {d.loanId}</span>
              </p>
            ))}
          </div>
        ) : null}

        {model.sections.map((section) => (
          <section
            key={section.id}
            className="rounded-lg border border-amber-400/10 overflow-hidden"
            data-review-section={section.id}
          >
            <div className="flex items-center justify-between bg-black/20 px-3 py-2">
              <span className="text-[11px] font-medium text-stone-200">{section.label}</span>
              <button
                type="button"
                onClick={() => onEditSection(section.label)}
                className="text-[10px] text-amber-300 hover:underline"
              >
                Edit
              </button>
            </div>
            <div className="divide-y divide-amber-400/5">
              {section.rows.map((row) => (
                <div
                  key={row.field}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-baseline gap-2 px-3 py-1.5"
                  data-review-field={row.field}
                  data-review-tag={row.tag}
                >
                  <span className="text-[10px] text-stone-500">{row.label}</span>
                  <span className="text-[11px] text-stone-200 truncate" title={row.value}>
                    {row.value}
                    {row.formula && row.tag === 'calculated' ? (
                      <span className="ml-1 text-[9px] text-stone-600">({row.formula})</span>
                    ) : null}
                  </span>
                  <span className={`rounded px-1.5 py-px text-[9px] ${tagClass(row.tag)}`}>
                    {row.tag === 'defaulted' && row.formula ? row.formula : reviewTagLabel(row.tag)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))}

        {openingNote?.trim() ? (
          <div
            className="rounded-lg border border-amber-400/10 bg-black/20 p-3"
            data-testid="manual-entry-review-note"
          >
            <p className="text-[11px] font-medium text-stone-300">Opening note</p>
            <p className="mt-1 whitespace-pre-wrap text-[10px] text-stone-400">{openingNote.trim()}</p>
          </div>
        ) : null}

        {model.backshopBlank.length ? (
          <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
            <p className="text-[11px] font-medium text-stone-300">Needed for Backshop, still blank</p>
            <p className="mt-1 text-[10px] text-stone-500">{model.backshopBlank.join(', ')}</p>
          </div>
        ) : null}

        {apiError ? <p className="text-[11px] text-red-300">{apiError}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onEdit}
            disabled={saving}
            className="btn-secondary flex items-center gap-1.5 text-[11px] px-3 py-1.5 disabled:opacity-50"
            data-testid="manual-entry-review-edit"
          >
            <ArrowLeft size={12} /> Edit
          </button>
          {duplicates && duplicates.length > 0 ? (
            <button
              type="button"
              onClick={onCreateAnyway}
              disabled={saving}
              className="btn-secondary flex items-center gap-1.5 text-[11px] px-3 py-1.5 disabled:opacity-50"
            >
              It is a different deal — create anyway
            </button>
          ) : null}
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="btn-primary flex items-center gap-1.5 text-[11px] px-4 py-1.5 disabled:opacity-50"
            data-testid="manual-entry-confirm"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            {saving ? 'Saving…' : 'Confirm & Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}
